import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { RbacApis } from 'apis/rbacAPI';
import { SearchableSelect } from 'components/common/SearchableSelect';
import { selectCustomGroups, selectFeatureGroupsTree } from 'store/dataReducers/rbacSlice';
import { FeatureGroupNode as FeatureGroupNodeType } from 'types/apis/rbacInterfaces';
import { flattenGroups } from './CreateFeatureGroupDialog';

interface AddExistingSubgroupDialogProps {
  open: boolean;
  /** The group the picked subgroup will be attached under. */
  target: { id: number; name: string } | null;
  onClose: () => void;
  onAdded: () => Promise<void> | void;
}

/** Ids of every node above `targetId` in the tree — attaching one of them as
 * a subgroup would reverse the chain and create a cycle. */
const findAncestorIds = (nodes: FeatureGroupNodeType[], targetId: number, path: number[] = []): number[] | null => {
  for (const node of nodes) {
    if (node.id === targetId) return path;
    const found = findAncestorIds(node.children || [], targetId, [...path, node.id]);
    if (found) return found;
  }
  return null;
};

export const AddExistingSubgroupDialog = ({ open, target, onClose, onAdded }: AddExistingSubgroupDialogProps) => {
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const fullTree = useSelector(selectFeatureGroupsTree);
  const customGroups = useSelector(selectCustomGroups);
  const [groupId, setGroupId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setGroupId('');
  }, [open]);

  const options = useMemo(() => {
    if (!target) return [];
    const excluded = new Set([target.id, ...(findAncestorIds(fullTree, target.id) || [])]);
    return flattenGroups(customGroups)
      .filter((g) => !excluded.has(g.id) && g.parent_group_id !== target.id)
      .map((g) => ({ value: String(g.id), label: `${' '.repeat(g.depth * 3)}${g.name}` }));
  }, [customGroups, fullTree, target]);

  const handleClose = () => {
    setGroupId('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!target || groupId === '') return;
    setSubmitting(true);
    const resp = await RbacApis.updateFeatureGroup(rc, groupId, { parent_group_id: target.id });
    setSubmitting(false);
    if (resp.success) {
      toast.success(`Added “${resp.data?.name}” as a subgroup of “${target.name}”`);
      await onAdded();
      handleClose();
    } else if (resp.error) {
      toast.error(resp.error);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add subgroup to “{target?.name}”</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <SearchableSelect
            label="Existing group"
            value={groupId === '' ? '' : String(groupId)}
            onChange={(value) => setGroupId(value === '' ? '' : Number(value))}
            options={options}
            fullWidth
            placeholder="Type to search groups…"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Moves the selected group under this one. Groups that would create a loop are hidden.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || groupId === ''}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
